import React, { FC, useEffect } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useForm, SubmitHandler, FieldValues } from 'react-hook-form';
import Stack from '../stack/Stack';
import { Button } from '../button';
import Input from './input/Input';
import Html from '../html/Html';
import Textarea from './textArea/Textarea';
import Checkbox from './checkbox/Checkbox';
import CallOutBanner from '../callOutBanner/CallOutBanner';
import Select from './select/Select';
import { useBackgroundColor } from '../../utils';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import Typography from '../typography/Typography';

// Interface

export interface FormProps {
  formType: 'Sales' | 'Partner' | 'Complaints';
  disclaimerText: string;
  successMessage: string;
  heading?: string;
  subHeading?: string;
  successHeading?: string;
  submitButtonText?: string;
}

type Inputs = {
  fullName: string;
  email: string;
  businessName?: string;
  phoneNumber: string;
  message?: string;
  disclaimer: boolean;
  accountType?: string;
  disSatisfied?: string;
  complaintAbout?: string;
  complaintDetail?: string;
};

// Styles

const StyledForm = styled.form`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
    ${useBackgroundColor('white')}
    border: solid 1px currentColor;
    padding: ${space.fluidSmallToXxLarge};
    border-radius: ${borderRadii.small};
    margin-left: auto;
    margin-right: auto;
  `};
`;

const StyledFormRow = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.xSmall};

    @media (min-width: ${media.large}) {
      gap: ${space.small};
    }
  `};
`;

// JSX

export const Form: FC<FormProps> = ({
  formType,
  disclaimerText,
  successMessage,
  heading,
  subHeading,
  successHeading,
  submitButtonText,
  ...rest
}) => {
  const { dataLayerPush } = useDataLayerPush();
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid, isSubmitSuccessful },
  } = useForm<Inputs>();

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    // TODO - do something with this data to post to the API
    console.log(data);

    dataLayerPush({
      event: 'form_success',
      formType,
    });
  };

  const onError = (errorFields: FieldValues) => {
    Object.keys(errorFields).forEach((key) => {
      const err = errorFields[key];
      dataLayerPush({
        event: 'form_error',
        formType,
        interactionType: `error on: ${key}`,
        errorMessage:
          err.type === 'required' || err.message === ''
            ? 'required'
            : err.message,
      });
    });
  };

  return (
    <StyledForm
      id={formType.toLowerCase()?.replace(' ', '')}
      onSubmit={handleSubmit(onSubmit, onError)}
      {...rest}
    >
      <Stack gap="fluidLargeToXxLarge">
        {heading || subHeading ? (
          <Stack gap="small">
            {heading && (
              <Typography component="h2" variant="headingMedium">
                {heading}
              </Typography>
            )}
            {subHeading && (
              <Typography component="p" variant="secondaryHeadingSmall">
                {subHeading}
              </Typography>
            )}
          </Stack>
        ) : (
          ''
        )}

        {isSubmitSuccessful ? (
          <CallOutBanner
            backgroundColor="lime"
            heading={successHeading || 'Thank you for your enquiry'}
            style="secondary"
            icon="Smile"
          >
            <Html>{successMessage}</Html>
          </CallOutBanner>
        ) : (
          <Stack gap="medium">
            <Stack gap="fluidXSmallToSmall">
              <StyledFormRow>
                <Input
                  {...register('fullName', { required: true })}
                  label="Full name"
                  id="fullName"
                  invalid={!!errors.fullName}
                />
                <Input
                  {...register('email', {
                    required: true,
                    pattern: {
                      value:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: 'Enter an email address in the correct format',
                    },
                  })}
                  label="Email address"
                  id="email"
                  errorMessage={errors.email?.message}
                  invalid={!!errors.email}
                />
                {formType !== 'Complaints' && (
                  <Input
                    {...register('businessName', { required: true })}
                    label="Business name"
                    id="businessName"
                    invalid={!!errors.businessName}
                  />
                )}
                <Input
                  {...register('phoneNumber', {
                    pattern: {
                      value: /^\d{11}$/,
                      message: 'Enter a UK mobile or landline number',
                    },
                  })}
                  label="Phone number"
                  id="phoneNumber"
                  invalid={!!errors.phoneNumber}
                  errorMessage={errors.phoneNumber?.message}
                />
                {formType === 'Complaints' && (
                  <>
                    <Select
                      label="Account type"
                      id="accountType"
                      options={[
                        { value: '', label: 'Please select' },
                        {
                          value: 'personal_debit',
                          label: 'Personal (Debit)',
                        },
                        {
                          value: 'business_debit',
                          label: 'Business (Debit)',
                        },
                        {
                          value: 'personal_credit_card',
                          label: 'Personal Credit Card',
                        },
                        {
                          value: 'business_credit_card',
                          label: 'Business Credit Card',
                        },
                        { value: 'currency_card', label: 'Currency Card' },
                        { value: 'third_party', label: 'Third Party' },
                      ]}
                      {...register('accountType', { required: true })}
                      invalid={!!errors.accountType}
                    />
                    <Select
                      label="Are you dis-satisfied and wanting to raise a complaint?"
                      id="disSatisfied"
                      options={[
                        { value: '', label: 'Please select' },
                        { value: 'yes', label: 'Yes' },
                        { value: 'no', label: 'No' },
                      ]}
                      {...register('disSatisfied', { required: true })}
                      invalid={!!errors.disSatisfied}
                    />
                    <Select
                      label="What would you like to complain about?"
                      id="complaintAbout"
                      options={[
                        { value: '', label: 'Please select' },
                        {
                          value: 'account_closure',
                          label: 'Account Closure',
                        },
                        {
                          value: 'application_issues',
                          label: 'Application Issues',
                        },
                        {
                          value: 'online_banking_issues',
                          label: 'Online Banking Issues',
                        },
                        { value: 'card_issues', label: 'Card Issues' },
                        { value: 'credit_related', label: 'Credit Related' },
                        {
                          value: 'transaction_dispute',
                          label: 'Transaction Dispute',
                        },
                        { value: 'fees_interest', label: 'Fees & Interest' },
                        { value: 'account_access', label: 'Account Access' },
                        {
                          value: 'inbound_outbound_payments',
                          label: 'Inbound/Outbound Payments',
                        },
                        { value: 'fraud', label: 'Fraud' },
                        {
                          value: 'servicing_personnel_issues',
                          label: 'Servicing/Personnel Issues',
                        },
                      ]}
                      {...register('complaintAbout', { required: true })}
                      invalid={!!errors.complaintAbout}
                    />
                  </>
                )}
              </StyledFormRow>
              {formType === 'Complaints' && (
                <Textarea
                  {...register('complaintDetail', { required: true })}
                  label="Complaint details"
                  id="complaintDetail"
                  invalid={!!errors.complaintDetail}
                />
              )}
              {formType !== 'Complaints' && (
                <Textarea
                  {...register('message', { required: true })}
                  label="Message"
                  id="message"
                  invalid={!!errors.message}
                />
              )}
            </Stack>
            <Checkbox
              label={disclaimerText}
              id="disclaimer"
              {...register('disclaimer', { required: true })}
              invalid={!!errors.disclaimer}
            />
            <Button
              type="submit"
              arrowRight
              variant="zemplerBlue"
              label={submitButtonText || 'Send your enquiry'}
            />
          </Stack>
        )}
      </Stack>
    </StyledForm>
  );
};

export default Form;
